import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { PartyCard } from "../components/ui/PartyCard";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../lib/api";
import type { Party } from "../lib/types";

export function Home() {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [myParties, setMyParties] = useState<Party[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [partiesLoading, setPartiesLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<{ parties: Party[]; total: number }>("/api/parties/public?limit=5")
      .then((res) => setParties(res.parties))
      .catch(() => {})
      .finally(() => setPartiesLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    apiClient
      .get<Party[]>("/api/parties")
      .then(setMyParties)
      .catch(() => {});
  }, [user]);

  return (
    <main className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 pt-14">
      {/* Hero */}
      <div className="relative flex flex-col items-center justify-center px-4 pt-32 pb-20 text-center overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-amber-400/10 dark:bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <h1 className="text-5xl font-bold mb-4">
          Who's coming to{" "}
          <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
            dinner?
          </span>
        </h1>
        <p className="text-stone-500 dark:text-stone-400 text-lg mb-10 max-w-md">
          {t("home.heroSubtitle")}
        </p>

        {isLoading ? null : user ? (
          <div className="flex items-center gap-3">
            {myParties.length < 5 ? (
              <Link to="/build">
                <Button variant="primary" className="text-base px-6 py-3">
                  {t("home.buildParty")}
                </Button>
              </Link>
            ) : (
              <span className="text-stone-400 dark:text-stone-600 text-sm">
                {t("home.partyLimitReached")}
              </span>
            )}
            <span className="text-stone-500 dark:text-stone-600 text-sm">
              {t("navbar.signedInAs", { name: user.name.split(" ")[0] })}
            </span>
          </div>
        ) : (
          <Button
            variant="primary"
            className="text-base px-6 py-3"
            onClick={() => {
              window.location.href = "/auth/google";
            }}
          >
            {t("auth.signInWithGoogle")}
          </Button>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 pb-24 flex flex-col gap-12">
        {/* Your parties / new party (logged in only) */}
        {!isLoading && user && (
          <div>
            <h2 className="text-stone-500 dark:text-stone-400 text-sm font-semibold uppercase tracking-wider mb-6">
              {t("home.yourParties")}
            </h2>
            <div className="flex flex-col gap-4">
              {myParties.map((party) => (
                <PartyCard
                  key={party.id}
                  party={party}
                  showHost={false}
                  to={
                    party.status === "draft"
                      ? `/build/${party.slug}`
                      : `/party/${party.slug}`
                  }
                  subtitle={t("card.guest", { count: party.guests.length })}
                  badge={
                    party.status === "draft" && (
                      <span className="bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                        {t("card.draft")}
                      </span>
                    )
                  }
                />
              ))}
              {myParties.length < 5 && (
                <Link
                  to="/build"
                  className="bg-white dark:bg-stone-900 border border-dashed border-stone-300 dark:border-stone-700 hover:border-amber-500 hover:bg-stone-50 dark:hover:bg-stone-800 rounded-xl p-5 flex items-center gap-4 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-full bg-stone-100 dark:bg-stone-800 border-2 border-dashed border-stone-300 dark:border-stone-700 group-hover:border-amber-500 flex items-center justify-center text-stone-400 dark:text-stone-500 group-hover:text-amber-500 transition-colors text-lg font-bold">
                    +
                  </div>
                  <span className="font-semibold text-stone-500 dark:text-stone-400 group-hover:text-stone-900 dark:group-hover:text-stone-100 transition-colors">
                    {t("home.newParty")}
                  </span>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Leaderboard — logged-out only */}
        {!isLoading && !user && (
          <div>
            <h2 className="text-stone-500 dark:text-stone-400 text-sm font-semibold uppercase tracking-wider mb-6">
              {t("nav.leaderboard")}
            </h2>

            {partiesLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-5 h-5 rounded-full border-2 border-stone-300 dark:border-stone-700 border-t-amber-500 animate-spin" />
              </div>
            ) : parties.length === 0 ? (
              <div className="text-stone-400 dark:text-stone-600 text-sm">
                {t("home.noParties")}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {parties.map((party, i) => {
                  const rankEl =
                    i === 0 ? (
                      <span className="text-xl">🥇</span>
                    ) : i === 1 ? (
                      <span className="text-xl">🥈</span>
                    ) : i === 2 ? (
                      <span className="text-xl">🥉</span>
                    ) : (
                      <span className="text-stone-400 dark:text-stone-600 text-sm font-semibold">
                        #{i + 1}
                      </span>
                    );
                  return (
                    <PartyCard
                      key={party.id}
                      party={party}
                      left={
                        <div className="w-7 text-center flex-shrink-0">
                          {rankEl}
                        </div>
                      }
                    />
                  );
                })}
              </div>
            )}
            {parties.length > 0 && (
              <div className="mt-6 text-center">
                <Link
                  to="/leaderboard"
                  className="text-sm text-amber-500 hover:text-amber-400 transition-colors"
                >
                  {t("home.viewLeaderboard")}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
